import React, {
  useEffect,
  useMemo,
  useState,
} from 'react';

import axios from 'axios';

import {
  Link,
} from 'react-router-dom';

import './ProgrammeContentAdminPage.css';

/* global process */

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL;

const CONTENT_URL =
  `${API_BASE_URL}/api/v1/admin/programme_contents`;

const ProgrammeContentAdminPage = () => {
  const [items, setItems] =
    useState([]);

  const [status, setStatus] =
    useState('loading');

  const [error, setError] =
    useState('');

  useEffect(() => {
    const loadContent = async () => {
      try {
        const response =
          await axios.get(
            CONTENT_URL
          );

        setItems(response.data);
        setStatus('ready');
      } catch (requestError) {
        setError(
          requestError.response?.data?.error ||
          requestError.message ||
          'Unable to load programme content.'
        );

        setStatus('failed');
      }
    };

    loadContent();
  }, []);

  const programmeItems = useMemo(
    () =>
      items.filter(
        (item) =>
          item.has_2026_content
      ),
    [items]
  );

  const historicalOnly = useMemo(
    () =>
      items.filter(
        (item) =>
          !item.has_2026_content
      ),
    [items]
  );

  const grouped = useMemo(() => {
    const groups = {};

    programmeItems.forEach((item) => {
      const key =
        item.pilar_id || 'other';

      if (!groups[key]) {
        groups[key] = [];
      }

      groups[key].push(item);
    });

    return groups;
  }, [programmeItems]);

  if (status === 'loading') {
    return (
      <main className="admin-page">
        <p>
          Loading programme content...
        </p>
      </main>
    );
  }

  return (
    <main className="admin-page">
      <header className="admin-page-header">
        <div>
          <p className="admin-eyebrow">
            Public programme content
          </p>

          <h1>Programme Content</h1>

          <p>
            Manage the bilingual content used
            to present ACE's 2026–2030
            programme while preserving
            historical records.
          </p>
        </div>

        <Link
          to="/homes/main"
          target="_blank"
          rel="noopener noreferrer"
          className="admin-secondary-link"
        >
          View public programme
        </Link>
      </header>

      {error && (
        <div className="pc-message error">
          {error}
        </div>
      )}

      <section className="pc-summary">
        <article>
          <span>
            Current programme records
          </span>

          <strong>
            {programmeItems.length}
          </strong>
        </article>

        <article>
          <span>Programme pillars</span>

          <strong>
            {
              Object.keys(grouped)
                .filter(
                  (key) =>
                    key !== 'other'
                )
                .length
            }
          </strong>
        </article>

        <article>
          <span>Main records</span>

          <strong>
            {
              programmeItems.filter(
                (item) => item.main
              ).length
            }
          </strong>
        </article>

        <article>
          <span>
            Historical-only records
          </span>

          <strong>
            {historicalOnly.length}
          </strong>
        </article>
      </section>

      {[1, 2, 3].map((pilarId) => {
        const pillarItems =
          grouped[pilarId] || [];

        if (!pillarItems.length) {
          return null;
        }

        const mainItem =
          pillarItems.find(
            (item) => item.main
          );

        const details =
          pillarItems.filter(
            (item) => !item.main
          );

        return (
          <section
            className="pc-pillar-section"
            key={pilarId}
          >
            <header className="pc-pillar-heading">
              <div>
                <p className="pc-eyebrow">
                  Programme area
                </p>

                <h2>
                  Pillar {pilarId}
                </h2>

                {mainItem && (
                  <p>
                    {mainItem.title_2026_en ||
                      mainItem.title_2026_fr}
                  </p>
                )}
              </div>

              <span>
                {pillarItems.length}
                {' '}
                content
                {pillarItems.length > 1
                  ? ' records'
                  : ' record'}
              </span>
            </header>

            <div className="pc-content-list">
              {pillarItems.map(
                (item) => (
                  <article
                    key={item.id}
                    className={
                      item.main
                        ? 'pc-content-row main'
                        : 'pc-content-row'
                    }
                  >
                    <div className="pc-content-role">
                      <span>
                        {item.main
                          ? 'Main'
                          : 'Detail'}
                      </span>

                      <small>
                        ID {item.id}
                      </small>
                    </div>

                    <div className="pc-content-text">
                      <h3>
                        {item.title_2026_en ||
                          'Untitled content'}
                      </h3>

                      <p className="pc-content-fr">
                        {item.title_2026_fr}
                      </p>

                      <p className="pc-preview">
                        {
                          (
                            item.content_2026_en ||
                            item.content_2026_fr ||
                            ''
                          ).slice(0, 260)
                        }

                        {
                          (
                            item.content_2026_en ||
                            item.content_2026_fr ||
                            ''
                          ).length > 260
                            ? '...'
                            : ''
                        }
                      </p>
                    </div>

                    <div className="pc-content-action">
                      <Link
                        to={
                          `/admin/programme-content/` +
                          `${item.id}/edit`
                        }
                      >
                        Edit content
                      </Link>
                    </div>
                  </article>
                )
              )}
            </div>
          </section>
        );
      })}

      {historicalOnly.length > 0 && (
        <section className="pc-history-note">
          <div>
            <p className="pc-eyebrow">
              Historical archive
            </p>

            <h2>
              Historical content preserved
            </h2>

            <p>
              {historicalOnly.length}
              {' '}
              older Home records remain in the
              database but are not used as
              fallback content by the current
              2026 programme views.
            </p>
          </div>
        </section>
      )}
    </main>
  );
};

export default ProgrammeContentAdminPage;
